import { useEffect } from 'react';

/**
 * The plugin the user works with in a conversation, which its agent is told about on every
 * turn: the app tab shown in the side panel, docked beside the conversation, with its selected
 * item, or an artifact of one of its types. Any conversation
 * works with any of them: what is shown only changes the context. The core writes it from what it
 * knows (the manifest, the selected item, the artifact), never from text a plugin supplies:
 * the plugin still never sees nor writes the conversation.
 */
export type PluginUse = {
  plugin: string;
  title: string;
  /** The item selected in the app's sidebar (a design, an automation). */
  item?: { id: string; title?: string | undefined } | undefined;
  /** The artifact of the app's type that is open. */
  artifact?: { path: string; title?: string | undefined } | undefined;
  /** Its skills' resources. */
  skills: readonly string[];
};

/**
 * The plugin last shown with each conversation: still its context once the user is back in the
 * conversation (an app's `ask` sends there), until another one is shown.
 */
const uses = new Map<string, PluginUse>();

/** An app tab or an artifact of `use.plugin` is shown with `conversation`. */
export function notePluginUse(conversation: string, use: PluginUse): void {
  uses.set(conversation, use);
}

/** Notes `use` in `conversation` while it holds (a tab or an artifact shown). */
export function usePluginUse(conversation: string | null, use: PluginUse | null): void {
  const key = use === null || conversation === null ? null : JSON.stringify([conversation, use]);
  useEffect(() => {
    if (key === null) return;
    const [noted, value] = JSON.parse(key) as [string, PluginUse];
    notePluginUse(noted, value);
  }, [key]);
}

/** Names and paths from plugins, as plain text of one line. */
function plain(value: string, max = 160): string {
  return value
    .replace(/[<>\p{Cc}]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

function sentence(use: PluginUse): string {
  const subject = [
    use.item === undefined
      ? null
      : `on ${use.item.title ? `"${plain(use.item.title)}" (${plain(use.item.id, 128)})` : `"${plain(use.item.id, 128)}"`}`,
    use.artifact === undefined
      ? null
      : `with the artifact ${use.artifact.title ? `"${plain(use.artifact.title)}" ` : ''}at ${plain(use.artifact.path, 512)}`,
  ].filter((part): part is string => part !== null);
  const skills = use.skills.map((skill) => plain(skill, 512));
  return [
    `The user is working with the ${plain(use.title)} plugin (${plain(use.plugin, 96)})${subject.length > 0 ? ` ${subject.join(', ')}` : ''}.`,
    skills.length > 0
      ? `When the request is about it, read its skill first (${skills.join(', ')}) and use its tools through crowdex_run.`
      : 'When the request is about it, use its tools through crowdex_run.',
  ].join('\n');
}

/** The reminder telling the agent of `conversation` which plugin the user works with. */
export function pluginContextReminder(conversation: string): string | undefined {
  const use = uses.get(conversation);
  if (use === undefined) return undefined;
  return ['<crowdex_plugin_context>', sentence(use), '</crowdex_plugin_context>'].join('\n');
}
